import React, { useId } from 'react';
import ModalShell from './ModalShell';

// Modals are never narrower than max-w-2xl; only the larger sizes widen past it.
const MODAL_SIZES = {
  sm: 'sm:max-w-4xl',
  md: 'sm:max-w-4xl',
  lg: 'sm:max-w-4xl',
  xl: 'sm:max-w-4xl',
  '2xl': 'sm:max-w-5xl',
  '3xl': 'sm:max-w-7xl',
  '4xl': 'sm:max-w-screen-xl',
};

export const Modal = ({ isOpen, onClose, title, children, size = 'md', centerTitle = false, showCloseButton = true, maxWidth, footer }) => {
  const titleId = useId();
  return (
    <ModalShell isOpen={isOpen} onClose={onClose} panelClassName={maxWidth || MODAL_SIZES[size] || MODAL_SIZES.md} labelledBy={title ? titleId : undefined}>
      {(title || showCloseButton) && (
        <div className={`flex items-start gap-2 px-4 pt-2.5 pb-2 border-b border-gray-100 ${centerTitle ? 'justify-center' : 'justify-between'}`}>
          {title && (
            <h3 id={titleId} className={`text-base sm:text-lg font-semibold text-gray-900 ${centerTitle ? 'text-center' : ''} flex-1 min-w-0`}>
              {title}
            </h3>
          )}
          {showCloseButton && (
            <button
              type="button"
              onClick={onClose}
              className="flex-shrink-0 -mr-1 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-lg transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      )}
      <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-3 text-sm">
        {children}
      </div>
      {footer && (
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-4 py-2.5 border-t border-gray-100 bg-gray-50 safe-area-bottom">
          {footer}
        </div>
      )}
    </ModalShell>
  );
};

export const Card = ({ title, children, className = '', actions, bodyClassName = 'p-3' }) => {
  return (
    <div className={`bg-white border border-gray-200 shadow-sm rounded-xl overflow-hidden ${className}`}>
      {(title || actions) && (
        <div className="px-3 py-2 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
          {title && <h3 className="text-base font-semibold text-gray-900 break-words">{title}</h3>}
          {actions && (
            <div className="flex flex-wrap gap-2 w-full sm:w-auto sm:justify-end">
              {actions}
            </div>
          )}
        </div>
      )}
      <div className={bodyClassName}>{children}</div>
    </div>
  );
};

const BADGE_VARIANTS = {
  default: 'bg-gray-100 text-gray-700 ring-gray-200',
  primary: 'bg-blue-50 text-blue-700 ring-blue-200',
  success: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  warning: 'bg-amber-50 text-amber-800 ring-amber-200',
  danger: 'bg-red-50 text-red-700 ring-red-200',
  info: 'bg-sky-50 text-sky-700 ring-sky-200',
  purple: 'bg-violet-50 text-violet-700 ring-violet-200',
};

const BADGE_SIZES = {
  sm: 'px-1.5 py-0.5 text-xs',
  md: 'px-2 py-0.5 text-xs',
  lg: 'px-2.5 py-1 text-sm',
};

const BADGE_ROUNDING = {
  xs: 'rounded-sm',
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
  'rounded-full': 'rounded-full',
};

export const Badge = ({ children, variant = 'default', size = 'md', round = 'rounded-full', className = '', dot = false }) => (
  <span className={`inline-flex items-center gap-1.5 font-medium whitespace-nowrap ring-1 ring-inset ${BADGE_ROUNDING[round] || 'rounded-full'} ${BADGE_VARIANTS[variant] || BADGE_VARIANTS.default} ${BADGE_SIZES[size] || BADGE_SIZES.md} ${className}`}>
    {dot && <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" aria-hidden="true" />}
    {children}
  </span>
);

const STATUS_VARIANTS = { PENDING: 'warning', APPROVED: 'success', REJECTED: 'danger', CANCELLED: 'default' };

export const StatusBadge = ({ status, className = '' }) => (
  <Badge variant={STATUS_VARIANTS[status] || 'default'} dot className={className}>
    {status ? status.charAt(0) + status.slice(1).toLowerCase() : '—'}
  </Badge>
);

export const LoadingSpinner = ({ size = 'md', message = 'Loading...' }) => {
  const sizes = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
  };

  return (
    <div className="flex flex-col items-center justify-center p-3" role="status" aria-live="polite">
      <div className={`animate-spin rounded-full border-2 border-gray-200 border-t-blue-600 ${sizes[size] || sizes.md}`}></div>
      {message && <p className="mt-2 text-sm text-gray-600">{message}</p>}
    </div>
  );
};

export const Alert = ({ type = 'info', title, message, onClose }) => {
  const types = {
    info: { box: 'bg-blue-50 border-blue-200', icon: 'text-blue-500', title: 'text-blue-900', message: 'text-blue-800' },
    success: { box: 'bg-emerald-50 border-emerald-200', icon: 'text-emerald-500', title: 'text-emerald-900', message: 'text-emerald-800' },
    warning: { box: 'bg-amber-50 border-amber-200', icon: 'text-amber-500', title: 'text-amber-900', message: 'text-amber-800' },
    error: { box: 'bg-red-50 border-red-200', icon: 'text-red-500', title: 'text-red-900', message: 'text-red-800' },
  };

  const config = types[type] || types.info;

  return (
    <div className={`rounded-lg border px-3 py-2.5 ${config.box}`} role={type === 'error' ? 'alert' : 'status'}>
      <div className="flex gap-2.5">
        <svg className={`h-5 w-5 flex-shrink-0 ${config.icon}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
        <div className="flex-1 min-w-0">
          {title && <h3 className={`text-sm font-semibold ${config.title}`}>{title}</h3>}
          {message && <div className={`text-sm ${config.message} ${title ? 'mt-1' : ''}`}>{message}</div>}
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Dismiss"
            className={`flex-shrink-0 rounded-md p-1 ${config.icon} hover:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-current`}
          >
            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

// Standard page title row: title + description on the left, primary actions on the right
// (actions wrap below the title on phones)
export const PageHeader = ({ title, description, icon: Icon, actions, className = '' }) => (
  <div className={`flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between ${className}`}>
    <div className="flex items-start gap-2.5 min-w-0">
      {Icon && (
        <span className="hidden xs:flex flex-shrink-0 h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600 ring-1 ring-inset ring-blue-100">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
      )}
      <div className="min-w-0">
        <h1 className="text-lg sm:text-xl font-semibold text-gray-900 tracking-tight truncate">{title}</h1>
        {description && <p className="mt-0.5 text-sm text-gray-500">{description}</p>}
      </div>
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2 sm:justify-end">{actions}</div>}
  </div>
);

// Underline tabs; scroll horizontally on small screens instead of wrapping
export const Tabs = ({ tabs, active, onChange, className = '', ariaLabel = 'Sections' }) => (
  <div className={`border-b border-gray-200 ${className}`}>
    <nav className="-mb-px flex gap-1 sm:gap-2 overflow-x-auto no-scrollbar" role="tablist" aria-label={ariaLabel}>
      {tabs.map(({ id, label, icon: Icon, count }) => {
        const selected = id === active;
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(id)}
            className={`group inline-flex items-center gap-2 whitespace-nowrap border-b-2 px-2.5 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:bg-blue-50 ${selected
              ? 'border-blue-600 text-blue-700'
              : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}`}
          >
            {Icon && <Icon className={`h-4 w-4 ${selected ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-500'}`} aria-hidden="true" />}
            {label}
            {count !== undefined && count !== null && (
              <span className={`rounded-full px-2 py-0.5 text-xs ${selected ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{count}</span>
            )}
          </button>
        );
      })}
    </nav>
  </div>
);

export const EmptyState = ({ icon: Icon, title = 'Nothing here yet', description, action, className = '' }) => (
  <div className={`flex flex-col items-center justify-center text-center px-4 py-6 ${className}`}>
    {Icon && (
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-400 mb-2">
        <Icon className="h-6 w-6" aria-hidden="true" />
      </span>
    )}
    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
    {description && <p className="mt-1 max-w-sm text-sm text-gray-500">{description}</p>}
    {action && <div className="mt-3">{action}</div>}
  </div>
);

export const Skeleton = ({ className = 'h-4 w-full' }) => (
  <div className={`animate-pulse rounded-md bg-gray-200 ${className}`} aria-hidden="true" />
);

const STAT_TONES = {
  blue: 'bg-blue-50 text-blue-600',
  green: 'bg-emerald-50 text-emerald-600',
  amber: 'bg-amber-50 text-amber-600',
  red: 'bg-red-50 text-red-600',
  violet: 'bg-violet-50 text-violet-600',
  gray: 'bg-gray-100 text-gray-600',
};

export const StatCard = ({ label, value, hint, icon: Icon, tone = 'blue', className = '' }) => (
  <div className={`flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm ${className}`}>
    {Icon && (
      <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${STAT_TONES[tone] || STAT_TONES.blue}`}>
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
    )}
    <div className="min-w-0">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500 truncate">{label}</p>
      <p className="text-lg sm:text-xl font-semibold text-gray-900 leading-tight">{value}</p>
      {hint && <p className="text-xs text-gray-500 truncate">{hint}</p>}
    </div>
  </div>
);

// Accessible on/off switch
export const Toggle = ({ checked, onChange, label, description, disabled = false, id }) => {
  const generatedId = useId();
  const inputId = id || generatedId;
  return (
    <div className={`flex items-start justify-between gap-3 ${disabled ? 'opacity-60' : ''}`}>
      <label htmlFor={inputId} className="min-w-0 cursor-pointer">
        <span className="block text-sm font-medium text-gray-900">{label}</span>
        {description && <span className="block text-xs sm:text-sm text-gray-500 mt-0.5">{description}</span>}
      </label>
      <button
        id={inputId}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed ${checked ? 'bg-blue-600' : 'bg-gray-300'}`}
        style={{ minHeight: 0, minWidth: 0 }}
      >
        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
      </button>
    </div>
  );
};
